import React, { useState, useEffect, useRef } from 'react';

const EnquiryFormPage = () => {
  const observerRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    enquiryType: '',
    productInterest: '',
    message: '',
    quantity: '',
    timeline: '',
    budget: '',
    hearAboutUs: '',
    newsletter: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.enquiryType) newErrors.enquiryType = 'Please select an enquiry type';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Thank you for your enquiry! We will get back to you within 24 hours.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        position: '',
        enquiryType: '',
        productInterest: '',
        message: '',
        quantity: '',
        timeline: '',
        budget: '',
        hearAboutUs: '',
        newsletter: false
      });
    } catch (error) {
      alert('There was an error submitting your enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const enquiryTypes = [
    'Product Information',
    'Technical Specification',
    'Price Quote',
    'Bulk Order',
    'Partnership Opportunity',
    'Quality Certification',
    'General Enquiry'
  ];

  const productInterests = [
    'Aluminium Circles/Blanks',
    'Cookware/Utensils',
    'Aluminium Sheets',
    'Aluminium Scraps',
    'All Products',
    'Custom Requirements'
  ];

  const budgetRanges = [
    'Under ₹1 Lakh',
    '₹1-5 Lakhs',
    '₹5-10 Lakhs',
    '₹10-50 Lakhs',
    'Above ₹50 Lakhs',
    'Prefer not to say'
  ];

  const timelineOptions = [
    'Immediate (Within 1 week)',
    'Short term (1-4 weeks)',
    'Medium term (1-3 months)',
    'Long term (3+ months)',
    'No specific timeline'
  ];

  const hearAboutUsOptions = [
    'Google Search',
    'Social Media',
    'Website',
    'Referral',
    'Trade Show',
    'Advertisement',
    'Other'
  ];

  return (
    <div className="enquiry-form-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .enquiry-form-page {
          font-family: 'Poppins', sans-serif;
          line-height: 1.6;
          color: #333;
          overflow-x: hidden;
        }

        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .form-section {
          padding: 100px 0;
          background: #F5F7FA;
        }

        .form-container {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 3rem;
          align-items: start;
        }

        .form-content {
          background: white;
          padding: 3rem;
          border-radius: 16px;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
        }

        .form-header {
          margin-bottom: 2rem;
        }

        .form-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #1E2329;
          margin-bottom: 0.5rem;
        }

        .form-header p {
          color: #666;
          line-height: 1.6;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          color: #4A5260;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .required {
          color: #e74c3c;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 1rem;
          font-family: 'Poppins', sans-serif;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #7B3FE4;
          box-shadow: 0 0 0 3px rgba(123, 63, 228, 0.1);
        }

        .form-input.error {
          border-color: #e74c3c;
        }

        .form-select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 1rem;
          font-family: 'Poppins', sans-serif;
          transition: all 0.3s ease;
          background: white;
          cursor: pointer;
        }

        .form-select:focus {
          outline: none;
          border-color: #7B3FE4;
          box-shadow: 0 0 0 3px rgba(123, 63, 228, 0.1);
        }

        .form-textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 1rem;
          font-family: 'Poppins', sans-serif;
          transition: all 0.3s ease;
          background: white;
          resize: vertical;
          min-height: 120px;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #7B3FE4;
          box-shadow: 0 0 0 3px rgba(123, 63, 228, 0.1);
        }

        .error-message {
          color: #e74c3c;
          font-size: 0.85rem;
          margin-top: 0.3rem;
        }

        .checkbox-group {
          display: flex;
          align-items: center;
          margin-top: 1rem;
        }

        .checkbox-input {
          width: 18px;
          height: 18px;
          margin-right: 0.75rem;
          cursor: pointer;
        }

        .checkbox-label {
          font-size: 0.95rem;
          color: #666;
          cursor: pointer;
          line-height: 1.4;
        }

        .submit-button {
          width: 100%;
          padding: 16px;
          background: #7B3FE4;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          font-family: 'Poppins', sans-serif;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
        }

        .submit-button:hover:not(:disabled) {
          background: #5A1FB3;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(123, 63, 228, 0.4);
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .sidebar {
          background: white;
          padding: 2.5rem;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          height: fit-content;
          position: sticky;
          top: 2rem;
        }

        .sidebar h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1E2329;
          margin-bottom: 1.5rem;
        }

        .contact-info {
          margin-bottom: 2rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
        }

        .contact-icon {
          width: 40px;
          height: 40px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          flex-shrink: 0;
        }

        .contact-icon i {
          font-size: 1rem;
          color: white;
        }

        .contact-details h4 {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 0.2rem;
        }

        .contact-details p {
          font-size: 0.9rem;
          color: #666;
        }

        .form-tips {
          background: #F5F7FA;
          padding: 1.5rem;
          border-radius: 8px;
          margin-top: 2rem;
        }

        .form-tips h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .tips-list {
          list-style: none;
        }

        .tips-list li {
          padding: 0.3rem 0;
          color: #666;
          position: relative;
          padding-left: 1.2rem;
          font-size: 0.9rem;
        }

        .tips-list li::before {
          content: '•';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-weight: bold;
        }

        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .container {
            padding: 0 1rem;
          }

          .form-container {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .form-content {
            padding: 2rem;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .sidebar {
            position: static;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-edit"></i>
          </div>
          <h1 className="animate-fade-in">Enquiry Form</h1>
          <p className="hero-subtitle animate-fade-in">
            Send us your detailed requirements and we'll get back to you within 24 hours with comprehensive information
          </p>
        </div>
      </section>

      <section className="form-section">
        <div className="container">
          <div className="form-container animate-on-scroll">
            <div className="form-content">
              <div className="form-header">
                <h2>Send Your Enquiry</h2>
                <p>Please fill out the form below with your requirements. Fields marked with * are required.</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      First Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`form-input ${errors.firstName ? 'error' : ''}`}
                      placeholder="Enter your first name"
                    />
                    {errors.firstName && <div className="error-message">{errors.firstName}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Last Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`form-input ${errors.lastName ? 'error' : ''}`}
                      placeholder="Enter your last name"
                    />
                    {errors.lastName && <div className="error-message">{errors.lastName}</div>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <div className="error-message">{errors.email}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${errors.phone ? 'error' : ''}`}
                      placeholder="+91 XXXXX XXXXX"
                    />
                    {errors.phone && <div className="error-message">{errors.phone}</div>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Company Name</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your company name"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Position/Designation</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your role/position"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Enquiry Type <span className="required">*</span>
                    </label>
                    <select
                      name="enquiryType"
                      value={formData.enquiryType}
                      onChange={handleInputChange}
                      className={`form-select ${errors.enquiryType ? 'error' : ''}`}
                    >
                      <option value="">Select enquiry type</option>
                      {enquiryTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                    {errors.enquiryType && <div className="error-message">{errors.enquiryType}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Product Interest</label>
                    <select
                      name="productInterest"
                      value={formData.productInterest}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select product category</option>
                      {productInterests.map((product, index) => (
                        <option key={index} value={product}>{product}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quantity Required</label>
                    <input
                      type="text"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g., 100 pieces, 5 tonnes"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Timeline</label>
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select timeline</option>
                      {timelineOptions.map((timeline, index) => (
                        <option key={index} value={timeline}>{timeline}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Budget Range</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select budget range</option>
                      {budgetRanges.map((budget, index) => (
                        <option key={index} value={budget}>{budget}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">How did you hear about us?</label>
                    <select
                      name="hearAboutUs"
                      value={formData.hearAboutUs}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select option</option>
                      {hearAboutUsOptions.map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label className="form-label">
                    Message/Requirements <span className="required">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-textarea ${errors.message ? 'error' : ''}`}
                    placeholder="Please describe your requirements, specifications, or any questions you have..."
                    rows="5"
                  />
                  {errors.message && <div className="error-message">{errors.message}</div>}
                </div>

                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="newsletter"
                    name="newsletter"
                    checked={formData.newsletter}
                    onChange={handleInputChange}
                    className="checkbox-input"
                  />
                  <label htmlFor="newsletter" className="checkbox-label">
                    I would like to receive updates about new products and industry news
                  </label>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending Enquiry...' : 'Send Enquiry'}
                </button>
              </form>
            </div>

            <div className="sidebar">
              <h3>Contact Information</h3>
              
              <div className="contact-info">
                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-phone"></i>
                  </div>
                  <div className="contact-details">
                    <h4>Phone</h4>
                    <p>+91 94370 31796</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="contact-details">
                    <h4>Email</h4>
                    <p>roshanprasad.sbmi@gmail.com</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="contact-details">
                    <h4>Response Time</h4>
                    <p>Within 24 hours</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon">
                    <i className="fas fa-map-marker-alt"></i>
                  </div>
                  <div className="contact-details">
                    <h4>Location</h4>
                    <p>Sudpara, Bolangir, Odisha</p>
                  </div>
                </div>
              </div>

              <div className="form-tips">
                <h4>Form Tips</h4>
                <ul className="tips-list">
                  <li>Provide detailed specifications for accurate quotes</li>
                  <li>Include quantity requirements and timeline</li>
                  <li>Mention any specific quality standards needed</li>
                  <li>Upload relevant documents if available</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnquiryFormPage;